import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { FiShoppingCart, FiZap, FiArrowLeft } from 'react-icons/fi';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';

const GiftCardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [giftCard, setGiftCard] = useState(null);
  const [selectedDenomination, setSelectedDenomination] = useState(0);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);

  // Category name mapping
  const categoryMap = {
    'A': 'Accessories',
    'B': 'Baby & Kids',
    'C': 'Communication',
    'E': 'Electronics',
    'F': 'Fashion & Apparel',
    'G': 'General Merchandise',
    'H': 'Home & Living',
    'M': 'Mobile & Telecom',
    'R': 'Restaurant & Dining',
    'T': 'Travel & Entertainment',
    'W': 'Wellness & Sports'
  };

  useEffect(() => {
    loadGiftCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadGiftCard = async () => {
    try {
      setLoading(true);
      console.log('Loading gift card with ID:', id);
      
      // Get directly from individual endpoint
      const res = await axios.get(`/api/giftcards/${id}`);
      console.log('Gift card response:', res.data);
      
      // API returns a single object, not an array
      setGiftCard(res.data);
      setSelectedDenomination(res.data.denominations[0]?.value || 0);
    } catch (err) {
      console.error('Error loading gift card:', err);
      console.error('Error response:', err.response?.data);
    }
    setLoading(false);
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const result = await addToCart(giftCard._id);
    if (result.success) {
      alert('Added to cart!');
      navigate('/cart');
    } else {
      alert(result.error);
    }
  };

  const handleQuickBuy = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    navigate('/checkout', { 
      state: { 
        giftCardId: giftCard._id, 
        denomination: selectedDenomination, 
        quickBuy: true 
      } 
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!giftCard) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-xl text-gray-500">Gift card not found</p>
          <button
            onClick={() => navigate('/catalog')}
            className="mt-4 text-primary hover:underline"
          >
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  const categoryName = categoryMap[giftCard.category] || giftCard.category;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/catalog')}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <FiArrowLeft />
        <span>Back to Catalog</span>
      </button>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Image Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <img
            src={giftCard.image}
            alt={giftCard.name}
            className="w-full h-96 object-cover"
          />
        </div>

        {/* Details Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold mb-2">{giftCard.name}</h1>
          
          <div className="flex items-center space-x-4 mb-4">
            <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
              {categoryName}
            </span>
            {giftCard.country && (
              <span className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                {giftCard.country}
              </span>
            )}
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Description</h2>
            <p className="text-gray-700 whitespace-pre-line">{giftCard.description}</p>
          </div>

          {/* Denomination Selector */}
          <div className="mb-6">
            <label className="block text-lg font-semibold mb-3">
              Select Amount
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {giftCard.denominations.map((denom, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedDenomination(denom.value)}
                  className={`px-4 py-3 border-2 rounded-lg font-semibold transition ${
                    selectedDenomination === denom.value
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                >
                  ${denom.value}
                </button>
              ))}
            </div>
          </div>

          {/* Price Display */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total Price:</span>
              <span className="text-2xl font-bold text-primary">
                ${selectedDenomination}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={handleAddToCart}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-md hover:bg-gray-300 transition flex items-center justify-center space-x-2 font-semibold"
            >
              <FiShoppingCart size={20} />
              <span>Add to Cart</span>
            </button>
            <button
              onClick={handleQuickBuy}
              className="flex-1 bg-primary text-white px-6 py-3 rounded-md hover:bg-blue-600 transition flex items-center justify-center space-x-2 font-semibold"
            >
              <FiZap size={20} />
              <span>Buy Now</span>
            </button>
          </div>

          {/* Additional Info */}
          {giftCard.brand && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                <span className="font-semibold">Brand:</span> {giftCard.brand}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCardDetail;
